import type {
  ShoppingItemWriteInput,
  ShoppingListItem,
  ShoppingPresenceUser,
} from '@recipes/contracts';
import { formatQuantityAmount } from '@recipes/domain';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@recipes/ui';
import { ChevronDown, ChevronRight, CloudOff, Eye, RefreshCw } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { currentFormatLocale, currentLocale } from '@/lib/i18n';
import type { LiveMode } from './hooks';

const BOUGHT_OPEN_KEY = 'shopping:bought-open';

/** The expanded state of "Bought" is a view setting of this device. */
export function useBoughtOpen(): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(BOUGHT_OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      if (open) localStorage.setItem(BOUGHT_OPEN_KEY, '1');
      else localStorage.removeItem(BOUGHT_OPEN_KEY);
    } catch {
      // private mode: the choice lives for this page only
    }
  }, [open]);
  return [open, setOpen];
}

/** Newest purchase first; items checked before the field existed go last. */
export const sortBought = (items: ShoppingListItem[]): ShoppingListItem[] =>
  [...items].sort(
    (a, b) =>
      (b.checkedAt ?? '').localeCompare(a.checkedAt ?? '') ||
      b.updatedAt.localeCompare(a.updatedAt),
  );

export function BoughtSection({
  count,
  open,
  onOpenChange,
  onClear,
  children,
}: {
  count: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClear: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  if (count === 0) return null;
  return (
    <section aria-labelledby="bought-heading" className="mb-4" data-testid="shopping-bought">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h2 id="bought-heading" className="text-sm font-semibold uppercase tracking-wide">
          <button
            type="button"
            className="flex min-h-[44px] items-center gap-1 text-muted-foreground"
            aria-expanded={open}
            aria-controls="bought-items"
            onClick={() => onOpenChange(!open)}
            data-testid="shopping-bought-toggle"
          >
            {open ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
            {t('shopping.bought.title', { count })}
          </button>
        </h2>
        {open && (
          <Button variant="ghost" size="sm" onClick={onClear} data-testid="shopping-bought-clear">
            {t('shopping.bought.clear')}
          </Button>
        )}
      </div>
      <ul id="bought-items" hidden={!open} className="divide-y rounded-lg border">
        {open && children}
      </ul>
    </section>
  );
}

const amountOf = (
  item: Pick<
    ShoppingListItem,
    'quantity' | 'quantityMax' | 'quantityText' | 'unitCode' | 'unitText'
  >,
) => formatQuantityAmount(item, currentLocale());

export interface ReactivationQuestion {
  bought: ShoppingListItem;
  input: ShoppingItemWriteInput;
}

/** "Milch – last time 2 l. Now: 1 l / 2 l as before". One tap; the new quantity is preselected. */
export function ReactivationSheet({
  question,
  onAnswer,
}: {
  question: ReactivationQuestion | null;
  onAnswer: (choice: 'NEW' | 'KEEP' | null) => void;
}) {
  const { t } = useTranslation();
  const before = question ? amountOf(question.bought) : '';
  const next = question
    ? amountOf({
        quantity: question.input.quantity ?? null,
        quantityMax: question.input.quantityMax ?? null,
        quantityText: question.input.quantityText ?? null,
        unitCode: question.input.unitCode ?? null,
        unitText: question.input.unitText ?? null,
      })
    : '';
  return (
    <Dialog open={Boolean(question)} onOpenChange={(open) => !open && onAnswer(null)}>
      <DialogContent closeLabel={t('common.close')} data-testid="shopping-reactivation">
        <DialogHeader>
          <DialogTitle>{question?.bought.name}</DialogTitle>
          <DialogDescription>
            {t('shopping.reactivation.question', { amount: before })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:justify-start">
          <Button autoFocus onClick={() => onAnswer('NEW')} data-testid="shopping-reactivation-new">
            {next
              ? t('shopping.reactivation.useNew', { amount: next })
              : t('shopping.reactivation.noAmount')}
          </Button>
          <Button
            variant="outline"
            onClick={() => onAnswer('KEEP')}
            data-testid="shopping-reactivation-keep"
          >
            {t('shopping.reactivation.keep', { amount: before })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Who else is here, whether we are live, and what still waits for the server. */
export function ListStatus({
  mode,
  presence,
  pendingCount,
  syncedAt,
  announcement,
}: {
  mode: LiveMode;
  presence: ShoppingPresenceUser[];
  pendingCount: number;
  syncedAt: string | null;
  announcement: string;
}) {
  const { t } = useTranslation();
  const names = presence.map((u) => u.displayName).filter(Boolean);
  const time = syncedAt
    ? new Intl.DateTimeFormat(currentFormatLocale(), {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(syncedAt))
    : null;
  return (
    <div className="mb-3 space-y-1 text-xs text-muted-foreground">
      {names.length > 0 && (
        <p className="flex items-center gap-1" data-testid="shopping-presence">
          <Eye aria-hidden className="size-3.5" />
          {t('shopping.presence', { count: names.length, names: names.join(', ') })}
        </p>
      )}
      {mode === 'offline' && (
        <p className="flex items-center gap-1" role="status" data-testid="shopping-offline">
          <CloudOff aria-hidden className="size-3.5" />
          {time ? t('shopping.offline.asOf', { time }) : t('shopping.offline.noSync')}
        </p>
      )}
      {pendingCount > 0 && (
        <p className="flex items-center gap-1" role="status" data-testid="shopping-pending">
          <RefreshCw aria-hidden className="size-3.5" />
          {t('shopping.offline.pending', { count: pendingCount })}
        </p>
      )}
      <p className="sr-only" aria-live="polite" data-testid="shopping-announcer">
        {announcement}
      </p>
    </div>
  );
}
